import {
  Box,
  Card,
  Grid,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { H3, H5 } from 'components/Typography';
import { useNavigate, useSearchParams } from 'react-router-dom';

import FlexRowAlign from 'components/flexbox/FlexRowAlign';
import { Flame, LayoutDashboard } from 'lucide-react';
import CommunityPostCard from './CommunityPostCard';
import CommunityPostTitle from './CommunityPostTitle';

const CategoryPostList = ({ postsData = [], onCardClick, ...props }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();

  // Get category from URL parameters
  const categoryParam = searchParams.get('category') || 'all';

  // Parse category for cases like 'gossip-leehanseol'
  let mainCategory = categoryParam;
  let subCategoryTitle = '';

  if (categoryParam.includes('-')) {
    const parts = categoryParam.split('-');
    mainCategory = parts[0];
    // You might want to look up the subcategory title from a map if needed
    // For now, let's assume we want to handle 'gossip' as main category
  }

  // Map category parameter to display title and icon
  const categoryMap = {
    all: { title: '전체', icon: '/static/icons/all.png' },
    best: {
      title: '베스트',
      icon: '/static/icons/best.png',
      subcategories: {
        general: '일반',
      },
    },
    free: {
      title: '자유',
      icon: '/static/icons/chatbubble.png',
      subcategories: {
        general: '일반',
        general2: '일반2',
        general3: '일반3',
      },
    },
    gossip: {
      title: '뒷담화',
      icon: '/static/icons/bamboo.png',
      subcategories: {
        leehanseol: '이한솔',
        kimchanseol: '김찬솔',
      },
    },
    info: {
      title: '꿀정보',
      icon: '/static/icons/honey.png',
      subcategories: {
        super: '개꿀정보',
        second: '2꿀정보',
      },
    },
    qna: {
      title: '질문답변',
      icon: '/static/icons/bamboo.png',
      subcategories: {
        general: '일반',
        general2: '일반2',
        general3: '일반3',
        general4: '일반4',
      },
    },
  };

  const currentCategoryData = categoryMap[mainCategory] || categoryMap.all;

  // Determine display title
  let displayTitle = currentCategoryData.title;
  let displaySubTitle = null;

  if (categoryParam.includes('-')) {
    const parts = categoryParam.split('-');
    const subId = parts.slice(1).join('-'); // Handle multipart sub IDs if needed, but basic split works for now

    if (currentCategoryData.subcategories?.[subId]) {
      const subTitle = currentCategoryData.subcategories[subId];
      displayTitle = currentCategoryData.title;
      displaySubTitle = subTitle;
    }
  }

  const currentCategory = {
    ...currentCategoryData,
    title: displayTitle,
    subTitle: displaySubTitle,
  };

  const handleCardClick = item => {
    if (onCardClick) {
      onCardClick(item);
    } else {
      navigate('/naver/callmetellme/community/community-detail');
    }
  };

  const handleTitleClick = () => {
    // Navigate to category list page with current category
    navigate(
      `/naver/callmetellme/community/category-list?category=${categoryParam}`
    );
  };

  // Default posts data if none provided
  const defaultPostsData = [
    {
      id: 1,
      title: '새로운 프로젝트 시작했습니다!',
      category: '자유게시판',
      content:
        '오늘부터 새로운 프로젝트를 시작하게 되었습니다. 많은 분들의 응원과 조언 부탁드려요. 함께 성장해나가요!',
      thumbnail: '/static/post-image/post-1.png',
      starCount: 3,
      likeCount: 15,
      commentCount: 8,
    },
    {
      id: 2,
      title: 'React Hook 사용법 질문드립니다',
      category: '질문답변',
      content:
        'useEffect와 useState를 함께 사용할 때 발생하는 문제에 대해 질문드립니다. 경험이 있으신 분들의 도움을 구합니다.',
      // No thumbnail - will render as CommunityPostTitle
      starCount: 1,
      likeCount: 7,
      commentCount: 12,
    },
    {
      id: 3,
      title: '코딩 테스트 합격 후기 공유',
      category: '후기',
      content:
        '지난 주 코딩 테스트에 합격했습니다! 준비 과정과 팁을 공유드릴게요. 도움이 되었으면 좋겠습니다.',
      thumbnail: '/static/post-image/post-2.png',
      starCount: 5,
      likeCount: 23,
      commentCount: 18,
    },
    {
      id: 4,
      title: 'JavaScript ES6+ 문법 정리',
      category: '정보공유',
      content:
        'JavaScript ES6 이상의 문법들을 정리해서 공유드립니다. 초보자분들께 도움이 되었으면 좋겠어요.',
      // No thumbnail - will render as CommunityPostTitle
      starCount: 2,
      likeCount: 19,
      commentCount: 6,
    },
    {
      id: 5,
      title: '프론트엔드 개발자 모임 참여하실 분?',
      category: '모임',
      content:
        '서울에서 프론트엔드 개발자들이 모여서 정보를 공유하고 네트워킹하는 모임을 만들려고 합니다. 관심 있으신 분들 연락주세요!',
      thumbnail: '/static/post-image/post-3.png',
      starCount: 4,
      likeCount: 31,
      commentCount: 25,
    },
    {
      id: 6,
      title: 'CSS Grid vs Flexbox 언제 사용할까?',
      category: '질문답변',
      content:
        'CSS Grid와 Flexbox의 차이점과 언제 어떤 것을 사용해야 하는지 궁금합니다. 실무 경험이 있으신 분들의 의견을 듣고 싶어요.',
      // No thumbnail - will render as CommunityPostTitle
      starCount: 0,
      likeCount: 11,
      commentCount: 9,
    },
    {
      id: 7,
      title: '포트폴리오 웹사이트 완성했습니다!',
      category: '자유게시판',
      content:
        '드디어 개인 포트폴리오 웹사이트를 완성했습니다. React와 TypeScript를 사용해서 만들었어요. 피드백 부탁드립니다!',
      thumbnail: '/static/post-image/post-4.png',
      starCount: 6,
      likeCount: 28,
      commentCount: 14,
    },
    {
      id: 8,
      title: 'Git 브랜치 전략에 대해 질문',
      category: '질문답변',
      content:
        '팀 프로젝트에서 Git 브랜치 전략을 어떻게 세워야 할지 고민입니다. Git Flow vs GitHub Flow 중 어떤 것을 추천하시나요?',
      // No thumbnail - will render as CommunityPostTitle
      starCount: 1,
      likeCount: 9,
      commentCount: 7,
    },
    {
      id: 9,
      title: 'Next.js 13 App Router 사용 후기',
      category: '정보공유',
      content:
        'Next.js 13의 새로운 App Router를 사용해보고 느낀 점들을 공유드립니다. 장단점과 주의사항들을 정리했어요.',
      thumbnail: '/static/post-image/post-5.png',
      starCount: 3,
      likeCount: 16,
      commentCount: 11,
    },
    {
      id: 10,
      title: '개발자 취업 준비 스터디 모집',
      category: '모임',
      content:
        '개발자 취업을 준비하는 분들과 함께 스터디를 진행하려고 합니다. 코딩 테스트, 면접 준비 등을 함께 해요!',
      // No thumbnail - will render as CommunityPostTitle
      starCount: 2,
      likeCount: 22,
      commentCount: 19,
    },
  ];

  const dataToUse = postsData.length > 0 ? postsData : defaultPostsData;

  // Height balancing logic: distribute posts to maintain similar total height
  const balancePostsByHeight = posts => {
    // Separate posts by type (with/without thumbnail)
    const postsWithThumbnail = posts.filter(post => post.thumbnail);
    const postsWithoutThumbnail = posts.filter(post => !post.thumbnail);

    // Calculate height weights (thumbnail posts are ~2x taller)
    const thumbnailWeight = 2;
    const titleWeight = 1;
    const maxHeightPerColumn = 2 * thumbnailWeight + 2 * titleWeight; // Max height = 2 thumbnails + 2 without = 6 units

    // Create balanced distribution
    const leftColumn = [];
    const rightColumn = [];
    let leftHeight = 0;
    let rightHeight = 0;

    // Helper function to add post to column with lower height
    const addToLowerHeightColumn = post => {
      const postHeight = post.thumbnail ? thumbnailWeight : titleWeight;

      // Check if adding this post would exceed max height for either column
      const leftWouldExceed = leftHeight + postHeight > maxHeightPerColumn;
      const rightWouldExceed = rightHeight + postHeight > maxHeightPerColumn;

      // If both columns would exceed, don't add the post
      if (leftWouldExceed && rightWouldExceed) {
        return;
      }

      // If only one column would exceed, add to the other
      if (leftWouldExceed) {
        rightColumn.push(post);
        rightHeight += postHeight;
        return;
      }
      if (rightWouldExceed) {
        leftColumn.push(post);
        leftHeight += postHeight;
        return;
      }

      // If neither would exceed, add to the column with lower height
      if (leftHeight <= rightHeight) {
        leftColumn.push(post);
        leftHeight += postHeight;
      } else {
        rightColumn.push(post);
        rightHeight += postHeight;
      }
    };

    // First, distribute posts with thumbnails (they have more visual impact)
    postsWithThumbnail.forEach(addToLowerHeightColumn);

    // Then, distribute posts without thumbnails
    postsWithoutThumbnail.forEach(addToLowerHeightColumn);

    return { leftColumn, rightColumn };
  };

  const { leftColumn, rightColumn } = balancePostsByHeight(dataToUse);

  let padding;
  if (window.innerWidth > 1720 && !isTablet) {
    padding = '0px 0px';
  } else {
    padding = isTablet ? '0px 15px' : '0px 0px';
  }

  const StyledCard = styled(Card)(({ theme }) => ({
    width: '100%',
    padding: padding,
    backgroundColor: theme.palette.primary.lightGrey200,
    borderRadius: 0,
    border: 'none',
    [theme.breakpoints.down('sm')]: {
      '& > .MuiBox-root': {
        paddingBottom: 0,
      },
    },
  }));

  return (
    <StyledCard
      sx={{
        border: 'none',
        overflow: 'visible',
        ...props.sx,
      }}
    >
      <Box
        sx={{
          width: '100%',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            backgroundColor: '#fff',
            padding: isMobile ? '18px' : '26px',
            borderRadius: '24px',
            boxShadow: '1px 4px 6px rgba(0, 0, 0, 0.05)',
          }}
        >
          {isMobile ? (
            // Mobile Layout: Single column with title and posts
            <Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <FlexRowAlign
                  justifyContent="space-between"
                  width="100%"
                  onClick={handleTitleClick}
                  sx={{ cursor: 'pointer' }}
                >
                  <FlexRowAlign>
                    <img
                      src={currentCategory.icon}
                      alt="icon"
                      style={{
                        width: '24px',
                        height: '24px',
                        marginRight: '8px',
                      }}
                    />
                    <H3
                      style={{
                        fontSize: '18px',
                      }}
                    >
                      {currentCategory.title}
                      {currentCategory.subTitle && (
                        <span
                          style={{
                            color: theme.palette.text.secondary,
                            fontSize: '18px',
                            fontWeight: 500,
                          }}
                        >
                          {' - '}
                          {currentCategory.subTitle}
                        </span>
                      )}
                    </H3>
                  </FlexRowAlign>
                </FlexRowAlign>
              </Box>

              {/* Show posts on mobile - render as card if has thumbnail, otherwise as title */}
              {dataToUse.slice(0, 6).map(
                (item, index) =>
                  item && (
                    <Box key={item.id} mb={index === 5 ? 1 : 3}>
                      {item.thumbnail ? (
                        <CommunityPostCard
                          item={item}
                          isMobile={isMobile}
                          onClick={handleCardClick}
                        />
                      ) : (
                        <CommunityPostTitle
                          item={item}
                          isMobile={isMobile}
                          onClick={handleCardClick}
                        />
                      )}
                    </Box>
                  )
              )}
            </Box>
          ) : (
            // Desktop Layout: Main title + Two columns with mini titles
            <Box>
              {/* Main Title */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={3}
              >
                <FlexRowAlign
                  justifyContent="space-between"
                  width="100%"
                  onClick={handleTitleClick}
                  sx={{ cursor: 'pointer' }}
                >
                  <FlexRowAlign>
                    <img
                      src={currentCategory.icon}
                      alt="icon"
                      style={{
                        width: '24px',
                        height: '24px',
                        marginRight: '8px',
                      }}
                    />
                    <H3
                      style={{
                        fontSize: '20px',
                        color: theme.palette.primary.mainBlue,
                      }}
                    >
                      {currentCategory.title}
                      {currentCategory.subTitle && (
                        <span
                          style={{
                            color: theme.palette.text.secondary,
                            fontSize: '18px',
                            fontWeight: 500,
                          }}
                        >
                          {' - '}
                          {currentCategory.subTitle}
                        </span>
                      )}
                    </H3>
                  </FlexRowAlign>
                </FlexRowAlign>
              </Box>

              {/* Two Columns with Mini Titles */}
              <Grid container spacing={3} mb={1}>
                {/* Left Column - 공지 */}
                <Grid item xs={12} md={6}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    sx={{
                      borderRight: '1px solid #e0e0e0',
                      paddingRight: 3,
                    }}
                  >
                    {/* Mini Title for Left Column */}
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <FlexRowAlign
                        justifyContent="space-between"
                        width="100%"
                        onClick={() => handleTitleClick('공지')}
                        sx={{ cursor: 'pointer' }}
                      >
                        <FlexRowAlign>
                          <LayoutDashboard
                            size={20}
                            color={theme.palette.primary.mainBlue}
                          />
                          <H5
                            pl={1}
                            sx={{
                              fontWeight: 'bold',
                              lineHeight: 1.3,
                              fontSize: isMobile ? '17px' : '17px',
                              color: theme.palette.primary.mainBlue,
                            }}
                          >
                            공지
                          </H5>
                        </FlexRowAlign>
                      </FlexRowAlign>
                    </Box>

                    {/* Left column posts - render based on balanced distribution */}
                    {leftColumn.map((item, index) => (
                      <Box
                        key={item.id}
                        mb={index === leftColumn.length - 1 ? 0 : 1}
                      >
                        {item.thumbnail ? (
                          <CommunityPostCard
                            item={item}
                            isMobile={isMobile}
                            onClick={handleCardClick}
                          />
                        ) : (
                          <CommunityPostTitle
                            item={item}
                            isMobile={isMobile}
                            onClick={handleCardClick}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                </Grid>

                {/* Right Column - 베스트 */}
                <Grid item xs={12} md={6}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    sx={{
                      paddingLeft: 1,
                    }}
                  >
                    {/* Mini Title for Right Column */}
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <FlexRowAlign
                        justifyContent="space-between"
                        width="100%"
                        onClick={() => handleTitleClick('베스트')}
                        sx={{ cursor: 'pointer' }}
                      >
                        <FlexRowAlign>
                          <Flame
                            size={20}
                            color={theme.palette.primary.mainBlue}
                          />
                          <H5
                            pl={1}
                            sx={{
                              fontWeight: 'bold',
                              lineHeight: 1.3,
                              fontSize: isMobile ? '17px' : '17px',
                              color: theme.palette.primary.mainBlue,
                            }}
                          >
                            베스트
                          </H5>
                        </FlexRowAlign>
                      </FlexRowAlign>
                    </Box>

                    {/* Right column posts - render based on balanced distribution */}
                    {rightColumn.map((item, index) => (
                      <Box
                        key={item.id}
                        mb={index === rightColumn.length - 1 ? 0 : 1}
                      >
                        {item.thumbnail ? (
                          <CommunityPostCard
                            item={item}
                            isMobile={isMobile}
                            onClick={handleCardClick}
                          />
                        ) : (
                          <CommunityPostTitle
                            item={item}
                            isMobile={isMobile}
                            onClick={handleCardClick}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Box>
    </StyledCard>
  );
};

export default CategoryPostList;
